import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useUpdateUser, useUnlockAccount } from "@/hooks/user";
import { RoleSelector } from "@/components/ui";
import { useTranslation } from 'react-i18next';
import { User, Role } from "@/types/user.types";
import React, { type FormEvent, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";


export interface UserEditModalProps {
  isOpen: boolean;
  closeModal: () => void;
  user: User;
}

const UserEditModal: React.FC<UserEditModalProps> = ({
  isOpen,
  closeModal,
  user,
}) => {
  const { t } = useTranslation();
  const [userData, setUserData] = useState<User>(user);
  const [selectedRoles, setSelectedRoles] = useState<string[]>(
    user.roles ? user.roles.map((role: Role) => role.id) : []
  );
  const [showUnlockDialog, setShowUnlockDialog] = useState(false);
  const updateUserMutation = useUpdateUser(() => {
    closeModal();
  });
  const unlockAccountMutation = useUnlockAccount(() => {
    setShowUnlockDialog(false);
    // Update local user data to reflect unlocked status
    setUserData(prev => ({ ...prev, isLocked: false, lockedUntil: undefined, failedLoginAttempts: 0 }));
  });

  useEffect(() => {
    setUserData(user);
    setSelectedRoles(user.roles ? user.roles.map((role: Role) => role.id) : []);
  }, [user]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setUserData((prev: User) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const updateData = {
      email: userData.email,
      firstName: userData.firstName,
      lastName: userData.lastName,
      status: userData.status,
      roles: selectedRoles
    };
    await updateUserMutation.mutateAsync({ 
      userId: userData.id, 
      userData: updateData 
    });
  };

  const handleUnlockAccount = async () => {
    await unlockAccountMutation.mutateAsync(userData.id);
  };

  return (
    <Dialog open={isOpen} onOpenChange={closeModal}>
      <DialogContent className="sm:rounded">
        <DialogHeader>
          <DialogTitle>{t('settings.editUser')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="main-label">
              {t('settings.firstName')}
            </label>
            <input
              name="firstName"
              value={userData.firstName}
              onChange={handleInputChange}
              className="main-input"
            />
          </div>
          <div className="mb-4">
            <label className="main-label">
              {t('settings.lastName')}
            </label>
            <input
              name="lastName"
              value={userData.lastName}
              onChange={handleInputChange}
              className="main-input"
            />
          </div>
          <div className="mb-4">
            <label className="main-label">
              {t('settings.email')}
            </label>
            <input
              name="email"
              value={userData.email}
              onChange={handleInputChange}
              type="email"
              className="main-input"
              disabled
            />
          </div>
          <div className="mb-4">
            <label className="main-label">
              {t('settings.status')}
            </label>
            <select
              name="status"
              value={userData.status}
              onChange={handleInputChange}
              className="main-input"
            >
              <option value="ENABLED">{t('settings.enabled')}</option>
              <option value="DISABLED">{t('settings.disabled')}</option>
            </select>
          </div>
          
          {/* Account Lock Status - Only show when account is locked */}
          {userData.isLocked && (
            <div className="mb-4">
              <label className="main-label">
                {t('common.accountStatus')}
              </label>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 rounded text-sm bg-red-100 text-red-800">
                  {t('common.locked')}
                </span>
                {(userData.failedLoginAttempts ?? 0) > 0 && (
                  <span className="text-sm text-gray-600">
                    ({t('common.failedLoginAttempts')}: {userData.failedLoginAttempts})
                  </span>
                )}
              </div>
              <div className="mt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowUnlockDialog(true)}
                  className="text-orange-600 border-orange-300 hover:bg-orange-50"
                >
                  {t('common.unlockAccount')}
                </Button>
              </div>
            </div>
          )}
          <div className="mb-4">
            <RoleSelector
              selectedRoles={selectedRoles}
              onRolesChange={setSelectedRoles}
              label={t('settings.roles')}
              placeholder={t('settings.selectRoles')}
            />
          </div>
          <button
            type="submit"
            className="main-dark-button float-right"
          >
            {
              updateUserMutation.isPending ? (
                <p>{t('common.saving')}</p>
              ) : (
                <p>{t('common.save')}</p>
              )
            }
          </button>
        </form>
      </DialogContent>
      
      {/* Unlock Account Confirmation Dialog */}
      <AlertDialog open={showUnlockDialog} onOpenChange={setShowUnlockDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.unlockAccount')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('common.unlockAccountConfirmation')}
              <br />
              <br />
              {t('common.unlockAccountDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnlockAccount}
              disabled={unlockAccountMutation.isPending}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {unlockAccountMutation.isPending ? t('common.saving') : t('common.unlockAccount')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

export default UserEditModal;
